// src/shared/changelog/components/categoryVariant.ts
import type { StatusBadgeVariant } from "@ui-components/Tag/StatusBadge";
import type { ChangeCategory } from "../types";

/**
 * Category → badge color, shared by every surface that shows a change's
 * category: the feed's `EntryCard` and the What's new modal (CTX-224).
 *
 * It lives here rather than in `types.ts` so the model stays free of a
 * dependency on the component library, and rather than in either component so
 * the two cannot drift into disagreeing about what a `fix` looks like.
 *
 * `security` and `breaking` use `error`, which `StatusBadge` renders through
 * the design system's `danger` tokens — the palette has no `error` family (see
 * `GenericInlineAlert`'s docblock).
 */
export const CATEGORY_VARIANT: Record<ChangeCategory, StatusBadgeVariant> = {
  feature: "info",
  improvement: "success",
  fix: "warning",
  security: "error",
  deprecation: "warning",
  breaking: "error",
};

/**
 * Category → the tile's surface, border and ink (CTX-224).
 *
 * Keyed by the same `ChangeCategory` as `CATEGORY_VARIANT` and following the
 * same family choices, so the badge and the artwork behind it can never
 * disagree about what a `fix` looks like. `StatusBadge` maps `info` onto the
 * brand tokens because the palette has no info family; this does the same
 * directly, which is why `feature` reads brand rather than blue.
 *
 * Note (CTX-532): the badge now sits *inside* this surface, and `StatusBadge`
 * paints the same background token each family maps to — so the pill reads as
 * bare text rather than as a chip. That is the accepted look; if the badge is
 * ever wanted to stand out, this is the map to change, not the badge.
 */
export const CATEGORY_SURFACE: Record<ChangeCategory, string> = {
  feature:
    "bg-semantic-bg-brand-secondary border-semantic-border-brand-secondary text-semantic-text-brand",
  improvement:
    "bg-semantic-bg-success-secondary border-semantic-border-success-secondary text-semantic-text-success",
  fix: "bg-semantic-bg-warning-secondary border-semantic-border-warning-secondary text-semantic-text-warning",
  security:
    "bg-semantic-bg-danger-secondary border-semantic-border-danger-secondary text-semantic-text-danger",
  deprecation:
    "bg-semantic-bg-warning-secondary border-semantic-border-warning-secondary text-semantic-text-warning",
  breaking:
    "bg-semantic-bg-danger-secondary border-semantic-border-danger-secondary text-semantic-text-danger",
};
